import React, { useState, useContext, useEffect} from "react";
import { AppRelevantDataContext } from '../../AppContext';
import axios from 'axios';
import { Doughnut } from "react-chartjs-2";
import { Col, Row, Typography, Card, Tabs, Statistic, Space, Button } from "antd";
import {
    CheckCircleOutlined,
    WarningOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined
} from '@ant-design/icons';
import { BsBell } from 'react-icons/bs';
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import {
    IDS_LeastAffectedParameter, IDS_MostAffectedParameter, IDS_TotalAlertsSent,
    IDS_CriticalAlerts, IDS_ActionsTakenOnAlerts, IDS_CriticalAlertsBreakdown, IDS_AlertDistributionWeek, IDS_AlertDistributionMonth,
    IDS_AlertSrvcIssue, IDS_LoginServerIssue, IDS_RegistNetworkError, IDS_NoAlertDistributionData, IDS_TVOC, IDS_Temperature, IDS_Humidity, IDS_Odour, IDS_AlertSummary
} from '../../VcLanguage';
import {NO_DEVC_FOUND_FOR_LOGGED_IN_USER} from '../../VcConstants';
import { getAPIHostURL } from '../../ClientConfig';

ChartJS.register(ArcElement, Tooltip, Legend);
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const VcAlertSummary = () => {
    const context = useContext(AppRelevantDataContext);
    const appRelevantDataContextValue = context;
    const t = appRelevantDataContextValue?.t || ((key) => key);
    const [collapsed, setCollapsed] = useState(false);

    // State variable to hold various alert summary data and UI flags
    const [state, setState] = useState({
            totalAlertsSent: 0,
            criticalAlertCount: 0,
            actionsTakenCount: 0,
            showAlertSummary: true,
            totalSpikes: 0,
            recievedAlertSummaryData: [],
            bCheckDeviceBlockedStatus: false,
            havingPvgToViewBlockedDeviceData: false,
            selectedTreeNodeID: "",
            LoggedInUserID: "",
            languageToViewIn: "",
            errors: {
                others: 'Please wait. Fetching AlertSummary for the Device from Server...'
            },
        });

    // State variable to store alert statistics per parameter
    const [alertStats,setAlertStats] = useState([
        { label: "NH3OD", dispLabel: t(IDS_Odour), alertCount: 0, criticalAlertCount: 0,color: "#5EA529" }, // Vibrant Pinks
        { label: "VOC", dispLabel: t(IDS_TVOC), alertCount: 0, criticalAlertCount: 0, color: "#6366F1" }, // Modern Blues
        { label: "TEMP", dispLabel: t(IDS_Temperature), alertCount: 0, criticalAlertCount: 0,color: "#FF692A" }, // Warm Yellows
        { label: "HUM", dispLabel: t(IDS_Humidity), alertCount: 0, criticalAlertCount: 0,color: "#21BCFF" }, // Fresh Greens
    ]);

    // Data for the doughnut chart representing alert distribution
    const doughnutData = {
        labels: alertStats.map((item) => item.dispLabel),
        datasets: [
            {
                data: alertStats.map((item) => item.alertCount),
                backgroundColor: alertStats.map((item) => item.color),
                hoverBackgroundColor: alertStats.map((item) => item.color),
                borderColor: '#ffffff',
                borderWidth: 2,
                hoverBorderWidth: 4,
            },
        ],
    };

    // Array to hold data for the overall alert statistics cards
    const alertData = [
        { id: IDS_TotalAlertsSent, value: state.totalAlertsSent, icon: <BsBell style={{ color: '#6A7282' }} /> },
        { id: IDS_CriticalAlerts, value: state.criticalAlertCount, icon: <WarningOutlined style={{ color: '#6A7282' }} /> },
        { id: IDS_ActionsTakenOnAlerts, value: state.actionsTakenCount, icon: <CheckCircleOutlined style={{ color: '#6A7282' }} /> },
    ];

    // State variable to manage the loading state of the component
    const [Loading, setLoading] = useState(true)

    // Function to get the appropriate icon based on the alert statistic ID
    const getIcon = (id) => {
        switch (id) {
            case "IDS_TotalAlertsSent":
                return <BsBell style={{ color: '#6A7282' }} />; // Blue
            case "IDS_CriticalAlerts":
                return <WarningOutlined style={{ color: '#6A7282' }} />; // Red
            case "IDS_ActionsTakenOnAlerts":
                return <CheckCircleOutlined style={{ color: '#6A7282' }} />; // Green
            default:
                return null;
        }
    };

    // State variable to hold data for the least and most affected parameter highlights
    const [parameterHighlights, setParameterHighlights] = useState([
        { id: 1, label: IDS_LeastAffectedParameter, value: "--", color: null, icon: <ArrowDownOutlined/> },
        { id: 2, label: IDS_MostAffectedParameter, value: "--", color: null, icon: <ArrowUpOutlined/> },
    ]);

    // Function to determine the parameters with the highest and lowest alert counts
    function getExtremeValueParameters(data) {
        // If no data is provided or the array is empty, return default values
        if (!data || data.length === 0) {
            return { highest: { name: "--", color: null }, lowest: { name: "--", color: null} };
        }
    
        let highestParam = null;
        let lowestParam = null;
        
        // Iterate through the alert statistics data 
        for (let i = 0; i < data.length; i++) {
            const currentValue = data[i].alertCount;
            const currentLabel = data[i].dispLabel;
            const currentColor = data[i].color;
    
            if (currentValue != null && currentLabel != null && currentColor != null) {
                // If highestParam is null, this is the first valid parameter, so set both highest and lowest to it
                if (highestParam === null) {
                    highestParam = data[i];
                    lowestParam = data[i];
                } else {
                    // If the current alert count is greater than the current highest, update highestParam
                    if (currentValue > highestParam.alertCount) {
                        highestParam = data[i];
                    }
                    // If the current alert count is less than the current lowest, update lowestParam
                    if (currentValue < lowestParam.alertCount) {
                        lowestParam = data[i];
                    }
                }
            }
        }
    
        // Return the highest and lowest parameters (or default values if none were found)
        return {
            highest: highestParam && highestParam.dispLabel != null && highestParam.color != null
                ? { name: highestParam.dispLabel, color: highestParam.color }
                : { name: "--", color: null },
            lowest: lowestParam && lowestParam.dispLabel != null && lowestParam.color != null
                ? { name: lowestParam.dispLabel, color: lowestParam.color }
                : { name: "--", color: null },
        };
    }
    
    // useEffect hook that runs once after the initial render (componentDidMount equivalent)
    useEffect(() => {

        getAlertSummaryData();
 
    }, []);

    // useEffect hook that runs when specific dependencies change
    useEffect(() =>{
        let currentState = state;
        let appRelevantDataContextValue = context;  // Get all the relevant data from AppContext
    
        if( appRelevantDataContextValue == null ||
            ("loggedInUserInfo" in appRelevantDataContextValue) == false ||
            appRelevantDataContextValue.loggedInUserInfo == null ||
            ("userID" in appRelevantDataContextValue.loggedInUserInfo) == false ||
            appRelevantDataContextValue.loggedInUserInfo.userID == null ||
            appRelevantDataContextValue.loggedInUserInfo.userID.length <= 0 ||
            ("selectedNodeInfo" in appRelevantDataContextValue) == false ||
            appRelevantDataContextValue.selectedNodeInfo == null ||
            ("nodeID" in appRelevantDataContextValue.selectedNodeInfo) == false ||
            appRelevantDataContextValue.selectedNodeInfo.nodeID == null ||
            appRelevantDataContextValue.selectedNodeInfo.nodeID.length <= 0
        ) {
            console.error("AlertLog:componentDidUpdate - Should not happen. AppRelevantDataContext does not have LoggedIn User information or Information about the Node to be Selected.");
            return; // Do not process further
        }

        if( currentState.LoggedInUserID != appRelevantDataContextValue.loggedInUserInfo.userID ||
            currentState.languageToViewIn != appRelevantDataContextValue.language.languageToViewIn ||
            currentState.selectedTreeNodeID != appRelevantDataContextValue.selectedNodeInfo.nodeID
        ) {

            // Showing loading page to user to avoid unnecessary clicks from the user while fetching data from the server.
            appRelevantDataContextValue.onChangeProcessingReq(true);
    
            // The selected tree node has changed. Update the state based on the new selected node.
            getAlertSummaryData();
        }
    }, [context, state.LoggedInUserID, state.selectedTreeNodeID, state.languageToViewIn]);

    // Asynchronous function to fetch alert summary data from the API
    const getAlertSummaryData = async (inModifiedState = null) => {

        let inAppRelevantDataContextValue = context; // Get all the relevant data from AppContext
        let selectedTreeNodeID = inAppRelevantDataContextValue.selectedNodeInfo.nodeID;
        let loggedInUserID = inAppRelevantDataContextValue.loggedInUserInfo.userID;
        let languageToViewIn = inAppRelevantDataContextValue.language.languageToViewIn;
        let IsSelectedNodeDevice = inAppRelevantDataContextValue.selectedNodeInfo.isDevc;
        let t = inAppRelevantDataContextValue.t;
        let arrDeviceIDs = [];
        arrDeviceIDs.push(selectedTreeNodeID);

        let modifiedState;
        if(inModifiedState == null) {
            modifiedState = state;
        } else {
            modifiedState = inModifiedState;
        } 
        modifiedState.selectedTreeNodeID = selectedTreeNodeID;
        modifiedState.LoggedInUserID = loggedInUserID;
        modifiedState.languageToViewIn = languageToViewIn;
        modifiedState.recievedAlertSummaryData = [];
        modifiedState.isDeviceBlocked = false;
        modifiedState.bCheckDeviceBlockedStatus = false;
        modifiedState.havingPvgToViewBlockedDeviceData = false;

        setAlertStats([
            { label: "NH3OD", dispLabel: t(IDS_Odour), alertCount: 0, criticalAlertCount: 0,color: "#5EA529" }, // Vibrant Pinks
            { label: "TVOC", dispLabel: t(IDS_TVOC), alertCount: 0, criticalAlertCount: 0, color: "#6366F1" }, // Modern Blues
            { label: "TEMP", dispLabel: t(IDS_Temperature), alertCount: 0, criticalAlertCount: 0,color: "#FF692A" }, // Warm Yellows
            { label: "HUM", dispLabel: t(IDS_Humidity), alertCount: 0, criticalAlertCount: 0,color: "#21BCFF" }, // Fresh Greens
        ]);

        // Get the Browser Timezone Offset
        let localTimeZone = (new Date()).getTimezoneOffset();

        // Incase the LHS Tree does not have any devices OR if it is a General user (a user without Devices) Logged in
        // and the User has clicked the node with ID 'NO_DEVC_FOUND_FOR_LOGGED_IN_USER' or If the selected node itself is not a valid device.
        // In this case no need to bring data from server as DeviceID is not valid and we have to show him a HTML page with message.
        // Also you have to update state with loggedinUser, selectedTreeNodeID as it will avoid triggering componentDidUpdate infinitely.
        if((selectedTreeNodeID != null && selectedTreeNodeID.length > 0 && selectedTreeNodeID == NO_DEVC_FOUND_FOR_LOGGED_IN_USER) || !IsSelectedNodeDevice) {
            setState({...modifiedState});
            inAppRelevantDataContextValue.onChangeProcessingReq(false); // Removing Loading spinner.
            return;
        }

        let jsonParams = {
            arrDeviceIDs: arrDeviceIDs,
        }

        await axios.post(`${getAPIHostURL()}/wclient/getAlertSummaryData`, jsonParams)
        .then(response => {
        inAppRelevantDataContextValue.onChangeProcessingReq(false); // Removing Loading spinner. 
        
        if(response.data.code == 'SUCCESS') {

            modifiedState.recievedAlertSummaryData = response.data.retrievedAlertSummaryData;

            if (modifiedState.recievedAlertSummaryData && Array.isArray(modifiedState.recievedAlertSummaryData) && modifiedState.recievedAlertSummaryData.length > 0) {
                
                const parameterCounts = modifiedState.recievedAlertSummaryData[1] || [];
                const criticalAlertBreakdown = modifiedState.recievedAlertSummaryData[3] || [];

                const newAlertStats = alertStats.map(stat => {
                    // Find the alert count for the current parameter
                    const foundAlertCount = parameterCounts.find(item => item.MeasuredParam === stat.label);
                    const alert = foundAlertCount ? parseInt(foundAlertCount.AlertCount) : null;

                    // Find the critical alert count for the current parameter
                    const foundCriticalAlertCount = criticalAlertBreakdown.find(item => item.MeasuredParam === stat.label);
                    const criticalalert = foundCriticalAlertCount ? parseInt(foundCriticalAlertCount.ParamCriticalAlertCount) : null;

                    return { ...stat, alertCount: alert, criticalAlertCount: criticalalert };
                });
                setAlertStats(newAlertStats);
    
                // Extract overall alert statistics
                modifiedState.totalAlertsSent = modifiedState.recievedAlertSummaryData[0][0]?.TotalAlertsSent || 0;
                modifiedState.criticalAlertCount = modifiedState.recievedAlertSummaryData[2][0]?.CriticalAlertCount || 0;
                modifiedState.actionsTakenCount = modifiedState.recievedAlertSummaryData[4][0]?.ActionsTakenCount || 0;
                modifiedState.showAlertSummary = modifiedState.recievedAlertSummaryData[0][0]?.TotalAlertsSent > 0;
    
                // Get most and least affected paramter details
                const extremeParams = getExtremeValueParameters(newAlertStats);
                
                setState({...modifiedState});

                // Update the parameter highlights based on the extreme values
                const newparameterHighlights = parameterHighlights.map(stat => {
                    if (stat.id === 1) {
                        return { ...stat, value: extremeParams.lowest?.name || '--', color: extremeParams.lowest?.color || null };
                    } else if (stat.id === 2) {
                        return { ...stat, value: extremeParams.highest?.name || '--', color: extremeParams.highest?.color || null };
                    } else {
                        return stat; // Return the original stat if its id doesn't match
                    }
                });
                setParameterHighlights(newparameterHighlights);
        
            } else {
                // If no alert summary data is received, reset the state related to alerts
                modifiedState.totalAlertsSent = 0;
                modifiedState.criticalAlertCount = 0;
                modifiedState.actionsTakenCount = 0;
                modifiedState.showAlertSummary = false;
                setState({...modifiedState});
            }

        } else {
            if(response.data.code == 'REQ_PARAMS_MISSING') {
                modifiedState.errors.others = t(IDS_AlertSrvcIssue);
            } else if(response.data.code == 'SQL_ERROR') {
                modifiedState.errors.others = t(IDS_LoginServerIssue);
            } else if(response.data.code == 'SERVER_EXPERIENCING_ISSUES') {
                modifiedState.errors.others = t(IDS_LoginServerIssue);
            } else if(response.data.code == 'DEVICE_IS_BLOCKED') {
                modifiedState.isDeviceBlocked = true;
                // Clear the interval if the Device is blocked so that it will not get the data from the server on every interval for the blocked device.
                // clearInterval(state.interval);
                // setGetAlertLogDataIntervalFlag(false);
            } else {
                console.log('Should not reach here');
                modifiedState.errors.others = t(IDS_LoginServerIssue);
            } 
        }
        setState({...modifiedState});
    })
        .catch(error => {
        inAppRelevantDataContextValue.onChangeProcessingReq(false); // Removing Loading spinner.
        console.log(error);
        console.log("Network error:");
        if (axios.isCancel(error)) {
            console.log('Axios request cancelled beacuse of too many requests being sent to the Server.');
        } else {
            modifiedState.errors.others = t(IDS_RegistNetworkError);
            setState({...modifiedState});
        }
        })
        setLoading(false);
    }

    // Function to render the Doughnut chart section
    const getDoughnutChartSection = () => (
        <Card bordered={false} style={{ borderRadius: '16px',padding: "1px", boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)', textAlign: 'center' }}>
            <Title level={5} style={{ marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                {t(IDS_AlertDistributionWeek)}
            </Title>
            {state.totalAlertsSent == 0 ? (
                <div style={{ height: "200px", width: "100%", maxWidth: '400px', margin: '0 auto', display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Text type="secondary">{t(IDS_NoAlertDistributionData)}</Text>
                </div>
            ) : (
                // Render the Doughnut chart if there is alert data
                <div style={{ height: "157px", width: "100%", maxWidth: '400px', margin: '0 auto' }}>
                    <Doughnut
                        data={doughnutData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            cutout: '45%',
                            plugins: {
                                legend: {
                                    position: "bottom",
                                    align: "center",
                                    labels: {
                                        boxWidth: 15,
                                        padding: 15,
                                        font: { size: 14, color: '#4B5563' },
                                    },
                                },
                                tooltip: {
                                    callbacks: {
                                        label: (context) => `${context.label}: ${context.parsed}`,
                                    }
                                }
                            },
                        }}
                    />
                </div>
            )}
        </Card>
    );

    // Function to render the parameter highlight cards (least and most affected)
    const getParameterHighlightCards = () => (
        <Row gutter={[16, 16]}>
            {parameterHighlights.map((item) => (
                <Col xs={24} sm={12} key={item.id}>
                    <div
                        bordered={false}
                        style={{
                            backgroundColor: item.color ? item.color + '20' : '#fff', // White background if item.color is null
                            borderRadius: '12px',
                            padding: "5px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",

                        }}
                    >
                        <Statistic
                            title={
                                <Text style={{
                                    color: item.color || 'black', // Black text if item.color is null
                                    fontWeight: 500,
                                    fontSize: '1em',
                                }}>
                                    {t(item.label)}
                                </Text>
                            }
                            value={item.value == null || item.value == undefined ? "--" : item.value}
                            valueStyle={{ color: item.color || 'black', fontWeight: 'bold', fontSize: '1.2em' }} // Black value color if item.color is null
                        />
                    </div>
                </Col>
            ))}
        </Row>
    );

    // Function to render the overall alert statistics section (Total, Critical, Actions Taken)
    const getAlertStatsSection = () => (
        <div>
            {alertData.map((item, index) => (
                <div
                    key={index}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 15px",
                        width: "100%",
                        borderRadius: "12px",
                        background: "#fff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        marginBottom: "10px",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "8px", fontSize: "1.2em", color: "#6B7280" }}>
                            {getIcon(item.id)}
                        </span>
                        <Text style={{ fontSize: "14px", fontWeight: "600", color: "#555" }}>
                            {t(item.id)}
                        </Text>
                    </div>
                    <Title level={4} style={{ margin: "0", fontWeight: "bold", color: "#222" }}>
                        {item.value}
                    </Title>
                </div>
            ))}
        </div>
    );

    // Function to render the critical alerts breakdown section (progress bar)
    const getBreakdownSection = () => {
        const hasCriticalAlerts = alertStats.some(param => param.criticalAlertCount > 0);

        return (
            <div
                style={{
                    textAlign: "left",
                    fontFamily: "Arial, sans-serif",
                    marginTop: "0rem",
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    padding: "0.7rem 1rem 0.1rem 1rem",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                }}
            >
                <h6 style={{ fontWeight: "bold", marginBottom: "10px" }}>{t(IDS_CriticalAlertsBreakdown)}</h6>
                <div style={{ display: "flex", alignItems: "left", gap: "5px" }}>
                    <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#1A0D3F" }}>
                        {state.criticalAlertCount}
                    </span>
                </div>
                <div
                    style={{
                        display: "flex",
                        width: "100%",
                        margin: "10px 0px",
                        height: "30px",
                        borderRadius: "5px",
                        overflow: "hidden",
                        border: "1px solid #ddd",
                    }}
                >
                    {hasCriticalAlerts ? (
                        // Render progress bars for each parameter with critical alerts
                        alertStats
                            .filter((param) => param.criticalAlertCount > 0)
                            .map((param, index, arr) => {
                                const width = state.criticalAlertCount > 0 ? (param.criticalAlertCount / state.criticalAlertCount) * 100 : 0;
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            width: `${width}%`,
                                            backgroundColor: param.color,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#fff",
                                            fontSize: "13px",
                                            fontWeight: "bold",
                                            borderRight: index !== arr.length - 1 ? "2px solid #fff" : "none", // <-- correct check
                                        }}
                                    >
                                        {param.criticalAlertCount}
                                    </div>
                                );
                            })
                    ) : (
                        // Display an empty bar if there are no critical alerts
                        <div
                            style={{
                                width: "100%",
                                backgroundColor: "#f0f0f0", // Light grey or any neutral color
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#666", // Darker grey for "--"
                                fontSize: "13px",
                                fontWeight: "bold",
                            }}
                        >
                        </div>
                    )}
                </div>
            </div>
        );
    };


    return (
            
        <div
            style={{
                padding: "10px",
                paddingTop: "15px",
                marginBottom: "8px",
                background: "#fafafa",
                borderRadius: "15px",
                boxShadow: "0 6px 12px rgba(0, 0, 0, 0.05)",
                position: "relative"   // <-- needed so the button can be absolute in corner
            }}
            >
            {/* Upper-right toggle button */}
            <Button
                type="text"
                onClick={() => setCollapsed(c => !c)}
                aria-expanded={!collapsed}
                aria-controls="alert-summary-content"
                style={{ position: "absolute", right: 8, top: 8, padding: 4, zIndex: 2 }}
                icon={collapsed ? <FaChevronDown style={{fontWeight: 600}}/> : <FaChevronUp style={{fontWeight: 600}}/>}
            />

            <div style={{ width: "100%" }}>
                <h6 level={1} style={{ textAlign: "center", marginBottom: "8px", fontWeight: "bold", color: "#333" }}>
                {t(IDS_AlertSummary)}
                </h6>
            </div>

            {/* content hidden when collapsed */}
            {!collapsed && (
                <div id="alert-summary-content">
                <Row gutter={[24, 24]} justify="center">
                    <Col xs={24} lg={12}>
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        {getDoughnutChartSection()}
                        {getParameterHighlightCards()}
                    </Space>
                    </Col>
                    <Col xs={24} lg={10}>
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        {getAlertStatsSection()}
                        {getBreakdownSection()}
                    </Space>
                    </Col>
                </Row>
                </div>
            )}
            </div>
    );
};

export default VcAlertSummary;